
import { PageContainer } from "@/components/ui/page-container";
import { EnhancedChatWindow } from "@/components/ai-assistance/EnhancedChatWindow";
import { TokenBalance } from "@/components/tokens/TokenBalance";
import { Brain, Zap, Globe, MessageCircle, Clock, Shield, Activity, CheckCircle, Mic } from "lucide-react";
import { useIsMobile } from "@/hooks/use-mobile";
import { cn } from "@/lib/utils";
import { SkinAnalysisSection } from "@/components/ai-assistance/SkinAnalysisSection";

const AiAssistance = () => {
  const isMobile = useIsMobile();
  
  return (
    <PageContainer 
      title="Assistant IA"
      variant="ai"
      intensity="high"
      className="py-6"
    >
      <div className="space-y-8">
        <div className="flex flex-col max-w-3xl mx-auto">
          {/* Token Balance Component */}
          <div className="mb-6">
            <TokenBalance showDonationButton={true} />
          </div>

          <div className="bg-blue-50 border border-blue-100 rounded-xl p-5 shadow-sm">
            <div className="flex items-start gap-3 mb-4">
              <div className="bg-blue-600 p-2 rounded-full text-white mt-1">
                <Brain className="h-5 w-5" />
              </div>
              <div>
                <h2 className="text-xl font-semibold text-blue-800 mb-1">Assistant médical intelligent</h2>
                <p className="text-blue-700">
                  Notre assistant médical IA est disponible 24/7 pour répondre à vos questions de santé avec des informations fiables et précises.
                </p>
                <p className="text-sm text-blue-600 mt-2">
                  💡 Chaque question consomme 1 token. Faites un don pour obtenir plus de tokens !
                </p>
              </div>
            </div>
            
            <div className="grid grid-cols-2 md:grid-cols-4 gap-3 mb-3">
              <div className={cn(
                "flex flex-col items-center p-3 rounded-lg transition-colors bg-white/70 hover:bg-white border border-blue-100",
                "hover:border-blue-200 hover:shadow-sm"
              )}>
                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center mb-2">
                  <Zap className="h-4 w-4 text-blue-600" />
                </div>
                <span className="text-sm font-medium text-blue-800">Réponses instantanées</span>
              </div>
              
              <div className={cn(
                "flex flex-col items-center p-3 rounded-lg transition-colors bg-white/70 hover:bg-white border border-blue-100",
                "hover:border-blue-200 hover:shadow-sm"
              )}>
                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center mb-2">
                  <Globe className="h-4 w-4 text-blue-600" />
                </div>
                <span className="text-sm font-medium text-blue-800">Multilingue</span>
              </div>
              
              <div className={cn(
                "flex flex-col items-center p-3 rounded-lg transition-colors bg-white/70 hover:bg-white border border-blue-100",
                "hover:border-blue-200 hover:shadow-sm"
              )}>
                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center mb-2">
                  <Clock className="h-4 w-4 text-blue-600" />
                </div>
                <span className="text-sm font-medium text-blue-800">Disponible 24/7</span>
              </div>
              
              <div className={cn(
                "flex flex-col items-center p-3 rounded-lg transition-colors bg-white/70 hover:bg-white border border-blue-100",
                "hover:border-blue-200 hover:shadow-sm"
              )}>
                <div className="h-8 w-8 rounded-full bg-gradient-to-br from-blue-100 to-blue-200 flex items-center justify-center mb-2">
                  <Mic className="h-4 w-4 text-blue-600" />
                </div>
                <span className="text-sm font-medium text-blue-800">Interface vocale</span>
              </div>
            </div>
            
            <div className="mt-3 flex justify-center">
              <div className="text-xs text-center text-blue-500 bg-blue-50 border border-blue-100 rounded-full px-3 py-1 inline-flex items-center">
                <Shield className="h-3 w-3 mr-1" />
                <span>Vos questions et données sont traitées de manière confidentielle</span>
              </div>
            </div>
          </div>

          {/* Add Skin Analysis Section before the chat window */}
          <div className="mt-6 mb-6">
            <SkinAnalysisSection />
          </div>
          
          <div className="glass-morphism rounded-lg overflow-hidden shadow-lg animate-fade-in mx-auto w-full" style={{ animationDelay: "0.2s" }}>
            <EnhancedChatWindow />
          </div>
        </div>
        
        {!isMobile && (
          <div className="grid grid-cols-2 gap-8 max-w-5xl mx-auto mt-12">
            <div className="bg-white border border-blue-100 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-start gap-3 mb-4">
                <div className="bg-indigo-100 p-2 rounded-full text-indigo-600">
                  <Activity className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-1">Analyse de symptômes</h3>
                  <p className="text-gray-600 text-sm">
                    Notre IA peut analyser vos symptômes et vous fournir des informations préliminaires 
                    sur les conditions possibles, tout en vous rappelant de consulter un professionnel 
                    de la santé pour un diagnostic définitif.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white border border-blue-100 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-start gap-3 mb-4">
                <div className="bg-indigo-100 p-2 rounded-full text-indigo-600">
                  <Mic className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-1">Interface vocale</h3>
                  <p className="text-gray-600 text-sm">
                    Parlez directement à l'assistant et recevez des réponses à vos questions médicales 
                    sans avoir à taper. Idéal pour les situations où vous ne pouvez pas utiliser vos mains.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white border border-blue-100 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-start gap-3 mb-4">
                <div className="bg-indigo-100 p-2 rounded-full text-indigo-600">
                  <CheckCircle className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-1">Vérification d'information</h3>
                  <p className="text-gray-600 text-sm">
                    L'assistant peut vérifier la validité des informations de santé et vous aider 
                    à distinguer les faits des mythes courants sur la médecine et la santé.
                  </p>
                </div>
              </div>
            </div>
            
            <div className="bg-white border border-blue-100 rounded-xl p-5 shadow-sm hover:shadow-md transition-shadow">
              <div className="flex items-start gap-3 mb-4">
                <div className="bg-indigo-100 p-2 rounded-full text-indigo-600">
                  <MessageCircle className="h-5 w-5" />
                </div>
                <div>
                  <h3 className="text-lg font-semibold text-gray-800 mb-1">Conseil personnalisé</h3>
                  <p className="text-gray-600 text-sm">
                    En fonction de votre profil et de vos besoins spécifiques, notre assistant 
                    peut vous proposer des recommandations adaptées pour améliorer votre santé et bien-être.
                  </p>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </PageContainer>
  );
};

export default AiAssistance;
